import { useState } from "react";
import { Button } from "@/components/ui/button";
import { BedDouble, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { AssignRoomDialog } from "./AssignRoomDialog";

const OPEN = ["confirmed", "pending"];

/**
 * ASSIGN-ROOM · one control for every list/card/detail: a prominent "Assign Room" button while the booking has no room,
 * or the room number with a "Change" affordance until the guest is checked in. Renders nothing for checked-in / closed bookings
 * (except the plain room number when `showRoomWhenLocked`). Multi-room group bookings carry rooms per line → not editable here.
 */
export function AssignRoomButton({ reservation: r, onAssigned, size = "sm", className = "", showRoomWhenLocked = true, stopPropagation = true, testIdPrefix = "" }) {
  const [open, setOpen] = useState(false);
  const { hasPermission } = useAuth();
  if (!r) return null;
  const isGroup = !!(r.is_multi_room && (r.rooms || []).length > 1);
  const editable = OPEN.includes(r.status) && !isGroup && hasPermission("reservations.assign_room");
  const roomNo = r.room_number || (r.rooms || [])[0]?.room_number;
  const guard = (e) => { if (stopPropagation) { e.stopPropagation(); e.preventDefault(); } };

  if (!editable) {
    return showRoomWhenLocked && roomNo ? <span className={`mono font-semibold text-hocp-textPrimary ${className}`} data-testid={`${testIdPrefix}room-locked-${r.code}`}>{roomNo}</span> : null;
  }
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} onClick={guard}>
      {roomNo ? (
        <>
          <span className="mono font-semibold text-hocp-textPrimary" data-testid={`${testIdPrefix}room-assigned-${r.code}`}>{roomNo}</span>
          <button type="button" onClick={(e) => { guard(e); setOpen(true); }} title="Change room" aria-label={`Change room for ${r.code}`}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-hocp-textMuted hover:text-hocp-deepTeal hover:bg-hocp-softIce transition-colors"
            data-testid={`${testIdPrefix}change-room-${r.code}`}><Pencil size={11} />Change</button>
        </>
      ) : (
        <Button size={size} onClick={(e) => { guard(e); setOpen(true); }} data-testid={`${testIdPrefix}assign-room-${r.code}`}
          className="bg-amber-500 hover:bg-amber-400 text-[#1A2425] font-semibold border border-amber-600/30 shadow-sm">
          <BedDouble className="w-3.5 h-3.5 mr-1.5" />Assign Room
        </Button>
      )}
      <AssignRoomDialog reservation={r} open={open} onOpenChange={setOpen} onAssigned={onAssigned} />
    </span>
  );
}
